<template>
  <HrPageLayout
    title="County Setup"
    subtitle="Manage county letterhead, logos and contact details used on official reports"
    icon="🏛️"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'System Management' },
      { label: 'County Setup' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="loadCompanyInfo">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-primary" :disabled="saving || loading" @click="saveCompanyInfo">
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="bx bx-save"></i>
        Save changes
      </button>
    </template>

    <div class="cs-page">
      <div v-if="statusMessage" class="cs-alert" :class="statusError ? 'cs-alert-error' : 'cs-alert-ok'">
        <i class="bx" :class="statusError ? 'bx-error-circle' : 'bx-check-circle'"></i>
        {{ statusMessage }}
      </div>

      <div v-if="loading" class="cs-loading">
        <div class="cs-spinner"></div>
        <span>Loading county settings…</span>
      </div>

      <form v-else class="cs-form" @submit.prevent="saveCompanyInfo">
        <div class="cs-card">
          <div class="cs-card-header">
            <i class="bx bx-building-house"></i>
            <div>
              <h3>Letterhead details</h3>
              <p>County name, department line and contacts shown at the top of CSV/PDF reports.</p>
            </div>
          </div>
          <div class="cs-card-body">
            <div class="cs-grid">
              <label class="cs-field">
                <span>County / organization name <em>*</em></span>
                <input v-model="companyInfo.companyName" type="text" required placeholder="COUNTY GOVERNMENT OF HOMA BAY" />
              </label>
              <label class="cs-field">
                <span>Department line</span>
                <input v-model="companyInfo.departmentLine" type="text" placeholder="Department of Public Service, Administration &amp; Employee Relations" />
              </label>
              <label class="cs-field">
                <span>Email</span>
                <input v-model="companyInfo.companyEmail" type="email" placeholder="info@homabay.go.ke" />
              </label>
              <label class="cs-field">
                <span>Phone / contact</span>
                <input v-model="companyInfo.companyContact" type="text" placeholder="Office telephone" />
              </label>
              <label class="cs-field">
                <span>Address / P.O. Box</span>
                <input v-model="companyInfo.companyAddress" type="text" placeholder="P.O. Box …, Homa Bay" />
              </label>
              <label class="cs-field">
                <span>Website</span>
                <input v-model="companyInfo.website" type="text" placeholder="https://www.homabay.go.ke" />
              </label>
              <label class="cs-field">
                <span>Helpline Number</span>
                <input v-model="companyInfo.helplineNumber" type="text" placeholder="e.g. 0800 720 123" />
              </label>
              <label class="cs-field cs-field-full">
                <span>Report footer text</span>
                <input v-model="companyInfo.reportFooter" type="text" placeholder="Official HRMIS document — County Government of Homa Bay" />
              </label>
              <label class="cs-field">
                <span>Watermark Text</span>
                <input v-model="companyInfo.watermarkText" type="text" placeholder="e.g. DRAFT or CONFIDENTIAL" />
              </label>
            </div>
          </div>
        </div>

        <div class="cs-card">
          <div class="cs-card-header">
            <i class="bx bx-image"></i>
            <div>
              <h3>Logos on reports</h3>
              <p>Upload logos, choose top-left / top-right / center placement, and optional caption under each logo.</p>
            </div>
          </div>
          <div class="cs-card-body">
            <div class="cs-logo-grid">
              <div class="cs-logo-block">
                <label class="cs-field">
                  <span>Primary logo</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change="onLogoPick($event, 'primary')" />
                </label>
                <label class="cs-field">
                  <span>Position on report</span>
                  <select v-model="companyInfo.logoPrimaryPosition">
                    <option value="left">Top left</option>
                    <option value="center">Top center</option>
                    <option value="right">Top right</option>
                  </select>
                </label>
                <label class="cs-field">
                  <span>Caption under logo</span>
                  <input v-model="companyInfo.logoPrimaryCaption" type="text" placeholder="e.g. Homa Bay County" />
                </label>
                <div class="cs-logo-preview" :class="'pos-' + companyInfo.logoPrimaryPosition">
                  <div v-if="primaryPreviewSrc" class="cs-logo-with-cap">
                    <img :src="primaryPreviewSrc" alt="Primary logo">
                    <span v-if="companyInfo.logoPrimaryCaption" class="cs-logo-cap">{{ companyInfo.logoPrimaryCaption }}</span>
                  </div>
                  <div v-else class="cs-logo-empty">
                    <i class="bx bx-image-alt"></i>
                    <span>No primary logo uploaded</span>
                  </div>
                </div>
                <button
                  v-if="primaryPreviewSrc"
                  type="button"
                  class="cs-clear"
                  @click="clearLogo('primary')"
                >Remove primary logo</button>
              </div>

              <div class="cs-logo-block">
                <label class="cs-field">
                  <span>Secondary logo (optional)</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change="onLogoPick($event, 'secondary')" />
                </label>
                <label class="cs-field">
                  <span>Position on report</span>
                  <select v-model="companyInfo.logoSecondaryPosition">
                    <option value="left">Top left</option>
                    <option value="center">Top center</option>
                    <option value="right">Top right</option>
                  </select>
                </label>
                <label class="cs-field">
                  <span>Caption under logo</span>
                  <input v-model="companyInfo.logoSecondaryCaption" type="text" placeholder="e.g. County coat of arms" />
                </label>
                <div class="cs-logo-preview" :class="'pos-' + companyInfo.logoSecondaryPosition">
                  <div v-if="secondaryPreviewSrc" class="cs-logo-with-cap">
                    <img :src="secondaryPreviewSrc" alt="Secondary logo">
                    <span v-if="companyInfo.logoSecondaryCaption" class="cs-logo-cap">{{ companyInfo.logoSecondaryCaption }}</span>
                  </div>
                  <div v-else class="cs-logo-empty">
                    <i class="bx bx-image-alt"></i>
                    <span>No secondary logo uploaded</span>
                  </div>
                </div>
                <button
                  v-if="secondaryPreviewSrc"
                  type="button"
                  class="cs-clear"
                  @click="clearLogo('secondary')"
                >Remove secondary logo</button>
              </div>
              
              <div class="cs-logo-block">
                <label class="cs-field">
                  <span>Watermark Image (optional)</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change="onLogoPick($event, 'watermark')" />
                </label>
                <div class="cs-logo-preview">
                  <div v-if="watermarkPreviewSrc" class="cs-logo-with-cap">
                    <img :src="watermarkPreviewSrc" alt="Watermark Image">
                  </div>
                  <div v-else class="cs-logo-empty">
                    <i class="bx bx-image-alt"></i>
                    <span>No watermark uploaded</span>
                  </div>
                </div>
                <button
                  v-if="watermarkPreviewSrc"
                  type="button"
                  class="cs-clear"
                  @click="clearLogo('watermark')"
                >Remove watermark</button>
              </div>

              <div class="cs-logo-block">
                <label class="cs-field">
                  <span>Login QR Code (optional)</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change="onLogoPick($event, 'qrCode')" />
                </label>
                <div class="cs-logo-preview">
                  <div v-if="qrCodePreviewSrc" class="cs-logo-with-cap">
                    <img :src="qrCodePreviewSrc" alt="QR Code">
                  </div>
                  <div v-else class="cs-logo-empty">
                    <i class="bx bx-qr-scan"></i>
                    <span>No QR Code uploaded</span>
                  </div>
                </div>
                <button
                  v-if="qrCodePreviewSrc"
                  type="button"
                  class="cs-clear"
                  @click="clearLogo('qrCode')"
                >Remove QR code</button>
              </div>
            </div>
            <p class="cs-hint">
              Accepted formats: PNG, JPG, WEBP, GIF (max ~8MB). Tip: primary top-left with caption “Homa Bay County”,
              secondary top-right. Settings apply to payroll, compliance, PAS and other branded PDFs after save.
            </p>
          </div>
        </div>

        <div class="cs-actions">
          <button type="button" class="hr-btn hr-btn-ghost" :disabled="saving" @click="loadCompanyInfo">Discard changes</button>
          <button type="submit" class="hr-btn hr-btn-primary" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bx bx-save"></i>
            Save letterhead &amp; logos
          </button>
        </div>
      </form>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

function absLogoUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u) || String(u).startsWith("data:") || String(u).startsWith("blob:")) return u;
  try {
    return new URL(u, authUrl.endsWith("/") ? authUrl : authUrl + "/").href;
  } catch (_) {
    return u;
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read image file"));
    reader.readAsDataURL(file);
  });
}

export default {
  name: "CountySetup",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      statusMessage: "",
      statusError: false,
      companyInfo: {
        companyName: "",
        companyAddress: "",
        companyContact: "",
        companyEmail: "",
        departmentLine: "",
        website: "",
        reportFooter: "",
        logoPrimaryUrl: "",
        logoSecondaryUrl: "",
        logoPrimaryPosition: "left",
        logoSecondaryPosition: "right",
        logoPrimaryCaption: "",
        logoSecondaryCaption: "",
        watermarkText: "",
        watermarkImageUrl: "",
        helplineNumber: "",
        qrCodeUrl: ""
      },
      logoPrimaryFile: null,
      logoSecondaryFile: null,
      watermarkFile: null,
      qrCodeFile: null,
      logoPrimaryPreview: "",
      logoSecondaryPreview: "",
      watermarkPreview: "",
      qrCodePreview: "",
      clearPrimary: false,
      clearSecondary: false,
      clearWatermark: false,
      clearQrCode: false
    };
  },
  computed: {
    primaryPreviewSrc() {
      return this.logoPrimaryPreview || absLogoUrl(this.companyInfo.logoPrimaryUrl);
    },
    secondaryPreviewSrc() {
      return this.logoSecondaryPreview || absLogoUrl(this.companyInfo.logoSecondaryUrl);
    },
    watermarkPreviewSrc() {
      return this.watermarkPreview || absLogoUrl(this.companyInfo.watermarkImageUrl);
    },
    qrCodePreviewSrc() {
      return this.qrCodePreview || absLogoUrl(this.companyInfo.qrCodeUrl);
    }
  },
  mounted() {
    this.loadCompanyInfo();
  },
  methods: {
    applyCompanyPayload(payload = {}) {
      this.companyInfo = {
        companyName: payload.companyName || "",
        companyAddress: payload.companyAddress || "",
        companyContact: payload.companyContact || "",
        companyEmail: payload.companyEmail || "",
        departmentLine: payload.departmentLine || "",
        website: payload.website || "",
        reportFooter: payload.reportFooter || "",
        logoPrimaryUrl: payload.logoPrimaryUrl || "",
        logoSecondaryUrl: payload.logoSecondaryUrl || "",
        logoPrimaryPosition: ["left", "center", "right"].includes(String(payload.logoPrimaryPosition || "").toLowerCase())
          ? String(payload.logoPrimaryPosition).toLowerCase()
          : "left",
        logoSecondaryPosition: ["left", "center", "right"].includes(String(payload.logoSecondaryPosition || "").toLowerCase())
          ? String(payload.logoSecondaryPosition).toLowerCase()
          : "right",
        logoPrimaryCaption: payload.logoPrimaryCaption || "",
        logoSecondaryCaption: payload.logoSecondaryCaption || "",
        watermarkText: payload.watermarkText || "",
        watermarkImageUrl: payload.watermarkImageUrl || "",
        helplineNumber: payload.helplineNumber || "",
        qrCodeUrl: payload.qrCodeUrl || ""
      };
      this.logoPrimaryFile = null;
      this.logoSecondaryFile = null;
      this.watermarkFile = null;
      this.qrCodeFile = null;
      this.logoPrimaryPreview = "";
      this.logoSecondaryPreview = "";
      this.watermarkPreview = "";
      this.qrCodePreview = "";
      this.clearPrimary = false;
      this.clearSecondary = false;
      this.clearWatermark = false;
      this.clearQrCode = false;
    },
    onLogoPick(event, which) {
      const file = event?.target?.files?.[0] || null;
      if (which === "primary") {
        this.logoPrimaryFile = file;
        this.logoPrimaryPreview = file ? URL.createObjectURL(file) : "";
        this.clearPrimary = false;
      } else if (which === "secondary") {
        this.logoSecondaryFile = file;
        this.logoSecondaryPreview = file ? URL.createObjectURL(file) : "";
        this.clearSecondary = false;
      } else if (which === "watermark") {
        this.watermarkFile = file;
        this.watermarkPreview = file ? URL.createObjectURL(file) : "";
        this.clearWatermark = false;
      } else if (which === "qrCode") {
        this.qrCodeFile = file;
        this.qrCodePreview = file ? URL.createObjectURL(file) : "";
        this.clearQrCode = false;
      }
    },
    clearLogo(which) {
      if (which === "primary") {
        this.logoPrimaryFile = null;
        this.logoPrimaryPreview = "";
        this.companyInfo.logoPrimaryUrl = "";
        this.clearPrimary = true;
      } else if (which === "secondary") {
        this.logoSecondaryFile = null;
        this.logoSecondaryPreview = "";
        this.companyInfo.logoSecondaryUrl = "";
        this.clearSecondary = true;
      } else if (which === "watermark") {
        this.watermarkFile = null;
        this.watermarkPreview = "";
        this.companyInfo.watermarkImageUrl = "";
        this.clearWatermark = true;
      } else if (which === "qrCode") {
        this.qrCodeFile = null;
        this.qrCodePreview = "";
        this.companyInfo.qrCodeUrl = "";
        this.clearQrCode = true;
      }
    },
    loadCompanyInfo() {
      this.loading = true;
      this.statusMessage = "";
      this.statusError = false;
      const data = new FormData();
      data.append("function", "getCompanyInfo");
      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res?.data?.success) {
            this.applyCompanyPayload(res.data.data?.companyInfo || {});
          } else {
            this.statusMessage = res?.data?.message || "Unable to load county settings.";
            this.statusError = true;
          }
        })
        .catch((err) => {
          this.loading = false;
          this.statusMessage = "Error loading county settings.";
          this.statusError = true;
          console.error(err);
        });
    },
    async saveCompanyInfo() {
      if (!this.companyInfo.companyName.trim()) {
        this.statusMessage = "County / organization name is required.";
        this.statusError = true;
        return;
      }
      this.saving = true;
      this.statusMessage = "";
      this.statusError = false;
      try {
        const data = new FormData();
        data.append("function", "saveCompanyInfo");
        data.append("companyName", this.companyInfo.companyName || "");
        data.append("companyAddress", this.companyInfo.companyAddress || "");
        data.append("companyContact", this.companyInfo.companyContact || "");
        data.append("companyEmail", this.companyInfo.companyEmail || "");
        data.append("departmentLine", this.companyInfo.departmentLine || "");
        data.append("website", this.companyInfo.website || "");
        data.append("reportFooter", this.companyInfo.reportFooter || "");
        data.append("logoPrimaryPosition", this.companyInfo.logoPrimaryPosition || "left");
        data.append("logoSecondaryPosition", this.companyInfo.logoSecondaryPosition || "right");
        data.append("logoPrimaryCaption", this.companyInfo.logoPrimaryCaption || "");
        data.append("logoSecondaryCaption", this.companyInfo.logoSecondaryCaption || "");
        data.append("watermarkText", this.companyInfo.watermarkText || "");
        data.append("helplineNumber", this.companyInfo.helplineNumber || "");
        if (this.clearPrimary) data.append("clearLogoPrimary", "1");
        if (this.clearSecondary) data.append("clearLogoSecondary", "1");
        if (this.clearWatermark) data.append("clearWatermarkImage", "1");
        if (this.clearQrCode) data.append("clearQrCode", "1");

        // Multipart file + base64 fallback so logos save even if $_FILES is empty
        if (this.logoPrimaryFile) {
          data.append("logoPrimary", this.logoPrimaryFile, this.logoPrimaryFile.name || "primary.png");
          try {
            const b64 = await fileToDataUrl(this.logoPrimaryFile);
            if (b64) data.append("logoPrimaryBase64", b64);
          } catch (_) { /* ignore */ }
        }
        if (this.logoSecondaryFile) {
          data.append("logoSecondary", this.logoSecondaryFile, this.logoSecondaryFile.name || "secondary.png");
          try {
            const b64 = await fileToDataUrl(this.logoSecondaryFile);
            if (b64) data.append("logoSecondaryBase64", b64);
          } catch (_) { /* ignore */ }
        }
        if (this.watermarkFile) {
          data.append("watermarkImage", this.watermarkFile, this.watermarkFile.name || "watermark.png");
          try {
            const b64 = await fileToDataUrl(this.watermarkFile);
            if (b64) data.append("watermarkImageBase64", b64);
          } catch (_) { /* ignore */ }
        }
        if (this.qrCodeFile) {
          data.append("qrCode", this.qrCodeFile, this.qrCodeFile.name || "qrcode.png");
          try {
            const b64 = await fileToDataUrl(this.qrCodeFile);
            if (b64) data.append("qrCodeBase64", b64);
          } catch (_) { /* ignore */ }
        }

        const res = await execute(data, authUrl);
        this.saving = false;
        if (res?.data?.success) {
          this.applyCompanyPayload(res.data.data?.companyInfo || {});
          this.statusMessage = "County letterhead, logos and contacts saved.";
          this.statusError = false;
        } else {
          // Partial path: show server message (e.g. logos not stored)
          if (res?.data?.data?.companyInfo) {
            this.applyCompanyPayload(res.data.data.companyInfo);
          }
          this.statusMessage = res?.data?.message || "Failed to save county settings.";
          this.statusError = true;
        }
      } catch (err) {
        this.saving = false;
        this.statusMessage = "Failed to save county settings.";
        this.statusError = true;
        console.error(err);
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.cs-page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 960px;
}
.cs-alert {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}
.cs-alert i { font-size: 1.15rem; }
.cs-alert-ok { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
.cs-alert-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

.cs-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem;
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
}
.cs-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid #e2e8f0;
  border-top-color: #0c4a6e;
  border-radius: 50%;
  animation: cs-spin 0.8s linear infinite;
}
@keyframes cs-spin { to { transform: rotate(360deg); } }

.cs-form { display: flex; flex-direction: column; gap: 1.25rem; }
.cs-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}
.cs-card-header {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 1.1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 1px solid #e2e8f0;
}
.cs-card-header > i {
  font-size: 1.5rem;
  color: #0c4a6e;
  margin-top: 0.1rem;
}
.cs-card-header h3 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}
.cs-card-header p {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}
.cs-card-body { padding: 1.25rem; }

.cs-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.15rem;
}
.cs-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
}
.cs-field em { color: #dc2626; font-style: normal; }
.cs-field-full { grid-column: 1 / -1; }
.cs-field input[type="text"],
.cs-field input[type="email"],
.cs-field input[type="file"],
.cs-field select {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 0.65rem 0.8rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #0f172a;
  outline: none;
}
.cs-field input:focus,
.cs-field select:focus {
  border-color: #0c4a6e;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(12, 74, 110, 0.12);
}

.cs-logo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}
.cs-logo-block {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}
.cs-logo-preview {
  min-height: 140px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  padding: 1rem;
}
.cs-logo-preview.pos-left { justify-content: flex-start; }
.cs-logo-preview.pos-center { justify-content: center; }
.cs-logo-preview.pos-right { justify-content: flex-end; }
.cs-logo-with-cap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  max-width: 100%;
}
.cs-logo-preview img {
  max-height: 88px;
  max-width: 160px;
  object-fit: contain;
}
.cs-logo-cap {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
  line-height: 1.25;
}
.cs-logo-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 600;
  width: 100%;
}
.cs-logo-empty i { font-size: 1.75rem; }
.cs-clear {
  align-self: flex-start;
  border: none;
  background: none;
  color: #b91c1c;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.cs-clear:hover { text-decoration: underline; }
.cs-hint {
  margin: 1rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.45;
}

.cs-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  flex-wrap: wrap;
}
.hr-btn-ghost {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  border-radius: 8px;
  padding: 0.55rem 1rem;
  font-weight: 600;
  cursor: pointer;
}
.hr-btn-ghost:hover:not(:disabled) { background: #e2e8f0; }
.hr-btn-ghost:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 720px) {
  .cs-grid,
  .cs-logo-grid { grid-template-columns: 1fr; }
  .cs-field-full { grid-column: auto; }
}
</style>
